import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/core/redux/store';
import { defaultAvatar } from '@/utils/imagepath';
import { postData } from '@/services/api.service';
import { setUserDetails } from '@/core/redux/commonSlice';
import toaster from '@/components/toaster/toaster';

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string().required('Phone is required'),
  image: yup
    .mixed()
    .nullable()
    .test('fileSize', 'File is too large', (value) => {
      if (!value || value.length === 0) return true;
      return value[0]?.size <= 5 * 1024 * 1024; // 5MB
    }),
});

type FormValues = {
  name: string;
  email: string;
  phone: string;
  image: FileList | null;
};

const AccountDetails = () => {
  const [loading, setLoading] = useState(false);
  const user = useSelector((state: RootState) => state.common.userDetails);
  console.log(user);

  const dispatch = useDispatch();

  useEffect(() => {
    console.log(user);
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      image: null,
    },
  });

  // Reset form when user data is available
  useEffect(() => {
    if (user) {
      reset({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        image: null,
      });
    }
  }, [user, reset]);

  const onSubmit = async (data: FormValues) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('email', data.email);
    formData.append('phone', data.phone);
    if (data.image && data.image.length > 0) {
      formData.append('image', data.image[0]);
    }

    try {
      setLoading(true);
      const response = await postData('update-profile', formData);
      if (response?.status == 200 || response?.status == 201) {
        toaster.success(response?.data?.message);
        const updatedImage = response.data?.image || user.image;
        dispatch(
          setUserDetails({
            ...user,
            name: data.name,
            email: data.email,
            phone: data.phone,
            image: updatedImage,
          })
        );
      }
    } catch (error) {
      console.error('Submission failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="col-md-9 tabs style1">
      <div className="row">
        <div className="col-md-12">
          <div className="user-dashboard-tab__content tab-content">
            <div
              className="tab-pane fade show active"
              id="account_details"
              role="tabpanel"
            >
              <div className="card-box">
              <form
                className="mar-top-40 row"
                onSubmit={handleSubmit(onSubmit)}
                encType="multipart/form-data"
              >
                <div className="form-group col-12">
                  <label htmlFor="name">
                    Name <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <input {...field} className="form-control" />
                    )}
                  />
                  {errors.name && (
                    <p className="text-danger">{errors.name.message}</p>
                  )}
                </div>

                <div className="form-group col-md-6">
                  <label htmlFor="email">
                    Email <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <input type="email" {...field} className="form-control" />
                    )}
                  />
                  {errors.email && (
                    <p className="text-danger">{errors.email.message}</p>
                  )}
                </div>

                <div className="form-group col-md-6">
                  <label htmlFor="phone">
                    Phone <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <input type="text" {...field} className="form-control" />
                    )}
                  />
                  {errors.phone && (
                    <p className="text-danger">{errors.phone.message}</p>
                  )}
                </div>

                <div className="form-group col-md-6">
                  <div className="mb-2 ac-pro-img">
                    <img
                      src={user?.image_path || defaultAvatar}
                      alt="Customer"
                    />
                  </div>
                  <Controller
                    name="image"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="file"
                        className="form-control"
                        onChange={(e) => field.onChange(e.target.files)}
                      />
                    )}
                  />
                  {errors.image && (
                    <p className="text-danger">{errors.image.message}</p>
                  )}
                </div>

                <div className="col-12">
                  <button
                    type="submit"
                    className="button style1 mt-3"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDetails;
